import React, { useState } from "react";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { verifyTokenAPI, redeemTokenAPI } from "../../actions/index";

// ─── Theme tokens (matching OrganizerDashboard) ──────────────────────────────
const T = {
  bg: "#faf8f5",
  surface: "#ffffff",
  surfaceWarm: "#f5f2ed",
  border: "rgba(0,0,0,0.08)",
  borderMed: "rgba(0,0,0,0.12)",
  orange: "#fa8128",
  orangeDim: "rgba(250,129,40,0.1)",
  orangeGlow: "rgba(250,129,40,0.06)",
  text: "#1a1710",
  textMuted: "rgba(26,23,16,0.42)",
  textSub: "rgba(26,23,16,0.6)",
  success: "#16a34a",
  successBg: "rgba(22,163,74,0.08)",
  error: "#dc2626",
  errorBg: "rgba(220,38,38,0.08)",
  warning: "#ea8c55",
  warningBg: "rgba(234,140,85,0.08)",
  radius: "12px",
};

// ─── Styled Components ────────────────────────────────────────────────────────
const Container = styled.div`
  padding: 20px;
  @media (max-width: 480px) {
    padding: 10px 20px;
  }
`;

const FormSection = styled.div`
  background: ${T.surface};
  border: 1px solid ${T.border};
  border-radius: ${T.radius};
  padding: 24px;
  margin-bottom: 24px;
  @media (max-width: 480px) {
    padding: 16px;
  }
`;

const FormTitle = styled.h3`
  font-family: "Georgia", serif;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${T.text};
  margin: 0 0 16px;
`;

const FormDescription = styled.p`
  font-size: 13px;
  color: ${T.textMuted};
  margin: 0 0 20px;
  line-height: 1.5;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
  width: 100%;
`;

const Label = styled.label`
  display: block;
  font-family: "Courier New", monospace;
  font-size: 11px;
  color: ${T.text};
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
`;

const InputWrapper = styled.div`
  display: flex;
  gap: 8px;
`;

const Input = styled.input`
  flex: 1;
  padding: 12px 14px;
  background: ${T.surface};
  border: 1px solid ${({ error }) => error ? T.error : T.borderMed};
  border-radius: 8px;
  color: ${T.text};
  font-size: 14px;
  outline: none;
  font-family: "Courier New", monospace;
  transition: all 0.2s;

  &:focus {
    border-color: ${({ error }) => error ? T.error : T.orange};
    box-shadow: 0 0 0 3px ${({ error }) => error ? "rgba(220,38,38,0.1)" : "rgba(250,129,40,0.1)"};
  }

  &::placeholder {
    color: ${T.textMuted};
  }

  &:disabled {
    background: ${T.surfaceWarm};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const Button = styled.button`
  padding: 12px 20px;
  background: ${({ variant, disabled }) =>
    disabled
      ? T.textMuted
      : variant === "success"
      ? T.success
      : variant === "danger"
      ? T.error
      : T.orange};
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: "Courier New", monospace;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s;
  opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const StatusBox = styled.div`
  padding: 14px 16px;
  border-radius: 8px;
  background: ${({ status }) => {
    switch (status) {
      case "success":
        return T.successBg;
      case "error":
        return T.errorBg;
      case "warning":
        return T.warningBg;
      case "info":
        return T.orangeGlow;
      default:
        return T.surfaceWarm;
    }
  }};
  border: 1px solid ${({ status }) => {
    switch (status) {
      case "success":
        return "rgba(22,163,74,0.2)";
      case "error":
        return "rgba(220,38,38,0.2)";
      case "warning":
        return "rgba(234,140,85,0.2)";
      case "info":
        return "rgba(250,129,40,0.2)";
      default:
        return T.border;
    }
  }};
  color: ${({ status }) => {
    switch (status) {
      case "success":
        return T.success;
      case "error":
        return T.error;
      case "warning":
        return T.warning;
      case "info":
        return T.orange;
      default:
        return T.text;
    }
  }};
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 16px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const StatusIcon = styled.div`
  font-size: 16px;
  flex-shrink: 0;
`;

const StatusContent = styled.div`
  flex: 1;
`;

const StatusTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

const StatusMessage = styled.div`
  font-size: 12px;
  opacity: 0.9;
`;

const TokenDetails = styled.div`
  background: ${T.surfaceWarm};
  border: 1px solid ${T.border};
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
  font-size: 13px;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  color: ${T.textMuted};
  font-weight: 600;
`;

const DetailValue = styled.span`
  color: ${T.text};
  font-family: "Courier New", monospace;
  word-break: break-all;
`;

const LoadingSpinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const TipsSection = styled.div`
  background: ${T.orangeGlow};
  border: 1px solid ${T.orangeDim};
  border-radius: ${T.radius};
  padding: 16px;
  margin-top: 20px;
  font-size: 12px;
  color: ${T.text};
`;

const TipsTitle = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  display: flex;
  gap: 6px;
  align-items: center;
`;

const TipsList = styled.ul`
  margin: 0;
  padding-left: 20px;

  li {
    margin-bottom: 6px;
    color: ${T.textSub};

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const TabsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

// ─── TokensTab Component ──────────────────────────────────────────────────────
const TokensTab = () => {
  const dispatch = useDispatch();
  const [tokenCode, setTokenCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [step, setStep] = useState("verify"); // "verify" or "redeem"

  const handleVerifyToken = async (e) => {
    e.preventDefault();

    if (!tokenCode.trim()) {
      setMessage({
        type: "error",
        title: "Empty field",
        body: "Please enter a token code.",
      });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const { response, data } = await dispatch(verifyTokenAPI(tokenCode.trim()));

      if (response.ok && data.valid) {
        setTokenData(data.token);
        setStep("redeem");
        setMessage({
          type: "success",
          title: "Token verified ✓",
          body: "This token is valid and ready to redeem.",
        });
      } else if (response.status === 400) {
        const errorMsg = data.token_code?.[0] || "Invalid token code";
        setMessage({
          type: "error",
          title: "Token invalid",
          body: errorMsg,
        });
      } else {
        setMessage({
          type: "error",
          title: "Verification failed",
          body: data.message || "Unable to verify token. Please try again.",
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        title: "Network error",
        body: "Failed to connect to server. Please check your connection.",
      });
      console.error("Token verification error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemToken = async () => {
    if (!tokenCode.trim() || !tokenData) {
      setMessage({
        type: "error",
        title: "Missing information",
        body: "Token data is missing. Please verify again.",
      });
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to redeem this token for "${tokenData.discount_title || "discount"}"?`
    );

    if (!confirmed) return;

    setLoading(true);

    try {
      const { response, data } = await dispatch(redeemTokenAPI(tokenCode.trim()));

      if (response.ok && data.success) {
        setMessage({
          type: "success",
          title: "Token redeemed successfully! 🎉",
          body: `The discount "${data.token.discount_title || "discount"}" has been applied and the user has been notified.`,
        });
        setTokenCode("");
        setTokenData(null);
        setStep("verify");
      } else if (response.status === 403) {
        setMessage({
          type: "error",
          title: "Not authorized",
          body: data.error || "You don't have permission to redeem tokens for this discount.",
        });
      } else if (response.status === 400) {
        const errorMsg = data.token_code?.[0] || data.message || "Unable to redeem token";
        setMessage({
          type: "error",
          title: "Redemption failed",
          body: errorMsg,
        });
      } else {
        setMessage({
          type: "error",
          title: "Redemption error",
          body: data.message || "An error occurred while redeeming the token.",
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        title: "Network error",
        body: "Failed to connect to server. Please check your connection.",
      });
      console.error("Token redemption error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setTokenCode("");
    setTokenData(null);
    setStep("verify");
    setMessage(null);
  };

  return (
    <Container>
      <TabsGrid>
        {/* Verification Form */}
        <FormSection>
          <FormTitle>🎫 Token Verification</FormTitle>
          <FormDescription>
            Enter a token code to verify its validity before redeeming it for a discount.
          </FormDescription>

          {message && (
            <StatusBox status={message.type}>
              <StatusIcon>
                {message.type === "success" && "✓"}
                {message.type === "error" && "✕"}
                {message.type === "warning" && "⚠"}
                {message.type === "info" && "ℹ"}
              </StatusIcon>
              <StatusContent>
                <StatusTitle>{message.title}</StatusTitle>
                <StatusMessage>{message.body}</StatusMessage>
              </StatusContent>
            </StatusBox>
          )}

          <form onSubmit={handleVerifyToken}>
            <FormGroup>
              <Label htmlFor="token-code">Token Code</Label>
              <InputWrapper>
                <Input
                  id="token-code"
                  type="text"
                  placeholder="e.g. 550e8400-e29b-41d4-a716-446655440000"
                  value={tokenCode}
                  onChange={(e) => setTokenCode(e.target.value)}
                  disabled={loading}
                  error={message?.type === "error" && step === "verify"}
                />
              </InputWrapper>
            </FormGroup>

            <div style={{ display: "flex", gap: "8px" }}>
              {step === "verify" ? (
                <Button type="submit" disabled={loading || !tokenCode.trim()}>
                  {loading && <LoadingSpinner />}
                  {loading ? "Verifying..." : "Verify Token"}
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="success"
                    onClick={handleRedeemToken}
                    disabled={loading}
                  >
                    {loading && <LoadingSpinner />}
                    {loading ? "Redeeming..." : "Redeem Token"}
                  </Button>
                  <Button
                    type="button"
                    variant="cancel"
                    onClick={handleReset}
                    disabled={loading}
                    style={{
                      background: T.textMuted,
                    }}
                  >
                    Reset
                  </Button>
                </>
              )}
            </div>
          </form>

          {/* Token Details Display */}
          {tokenData && (
            <TokenDetails>
              <DetailRow>
                <DetailLabel>Token ID:</DetailLabel>
                <DetailValue>{tokenData.id}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Discount:</DetailLabel>
                <DetailValue>{tokenData.discount_title || "N/A"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Status:</DetailLabel>
                <DetailValue>
                  {tokenData.is_redeemed ? (
                    <span style={{ color: T.success }}>✓ Redeemed</span>
                  ) : (
                    <span style={{ color: T.orange }}>Pending</span>
                  )}
                </DetailValue>
              </DetailRow>
              {tokenData.user_email && (
                <DetailRow>
                  <DetailLabel>User Email:</DetailLabel>
                  <DetailValue>{tokenData.user_email}</DetailValue>
                </DetailRow>
              )}
              {tokenData.redeemed_at && (
                <DetailRow>
                  <DetailLabel>Redeemed at:</DetailLabel>
                  <DetailValue>
                    {new Date(tokenData.redeemed_at).toLocaleString()}
                  </DetailValue>
                </DetailRow>
              )}
            </TokenDetails>
          )}

          {/* Tips Section */}
          <TipsSection>
            <TipsTitle>💡 Pro Tips</TipsTitle>
            <TipsList>
              <li>Use 6-digit codes or full UUIDs for token scanning</li>
              <li>Each token can only be redeemed once</li>
              <li>A notification will be sent to the user after redemption</li>
              <li>Check the redemption status anytime using the token ID</li>
            </TipsList>
          </TipsSection>
        </FormSection>

        {/* Quick Redeem Section */}
        <FormSection>
          <FormTitle>⚡ Quick Redeem</FormTitle>
          <FormDescription>
            Fast-track token redemption for scanning scenarios. Verify and redeem in one step.
          </FormDescription>

          <div
            style={{
              background: T.surfaceWarm,
              border: `2px dashed ${T.borderMed}`,
              borderRadius: "8px",
              padding: "24px",
              textAlign: "center",
              color: T.textMuted,
            }}
          >
            <div style={{ fontSize: "32px", marginBottom: "8px" }}>📱</div>
            <div style={{ fontSize: "13px", marginBottom: "8px" }}>
              Scan or paste a QR code here
            </div>
            <div style={{ fontSize: "11px", color: T.textSub }}>
              The form above supports scanning directly
            </div>
          </div>

          <TipsSection style={{ marginTop: "16px" }}>
            <TipsTitle>🔄 How it works</TipsTitle>
            <TipsList>
              <li>Paste or scan a token code in the verification field above</li>
              <li>Click "Verify Token" to check validity</li>
              <li>Click "Redeem Token" to complete the process</li>
              <li>Confirmation required before each redemption</li>
            </TipsList>
          </TipsSection>
        </FormSection>
      </TabsGrid>
    </Container>
  );
};

export default TokensTab;